import axios, { AxiosInstance } from 'axios'
import { isDev } from "../utils/DebugUtils";
import { normalizeAuthError } from "../utils/authErrors";

/* ------------- Core API ------------- */

/**
 * The class to interact with [Core](https://doc-core.getselect.co) API
 *
 * @class CoreApi
 */
class FetchApi {
  api!: AxiosInstance
  url!: string

  /**
   * Creates an instance of CoreApi.
   * @param {string} coreUrl
   * @param {string} language
   * @memberof CoreApi
   */
  constructor() {
    this.url = `https://${isDev() ? "staging" : "phoenix"}.selectdc1.com/v1`
    this.initApi()
  }

  /**
   * Reset api to default parameters
   *
   * @memberof CoreApi
   */
  initApi() {
    this.api = axios.create({
      baseURL: this.url,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
      timeout: 30000,
      validateStatus: (status) => {
        return status >= 200 && status < 300
      },
    })
  }

  startProviderAuth(provider: string, domain: string): Promise<any> {
    return this.api
    .get(`identity/provider/new?provider=${provider}&domain=${domain}`)
    .then((response) => {
      return response.data
    })
    .catch((response) => {
      throw Error(JSON.stringify(response?.response?.data));
    })
  };

  redirectProviderAuth(provider: string, code: string, linkId: string, userId: string, domain: string): Promise<any> {
    return this.api
      .get('identity/provider/redirect', {
        params: { provider, code, link_id: linkId, user_id: userId, domain },
      })
      .then((response) => {
        return response.data
      })
      .catch((response) => {
        if (response?.status === 400) {
            if (response?.response?.data?.message === "New user needs invitation to join") {
                throw new Error("New user needs invitation to join");
            } 
        }

        throw Error(JSON.stringify(response?.response?.data));
      })
  }

  invitationEmail(email: string, link_id: string, post_slug: string): Promise<any> {
    return this.api.post('invitation/landing-page-email', {
      email,
      link_id,
      ...(post_slug ? { post_slug } : {}),
    })
    .then((response) => {
      return response.data
    })
    .catch((response) => {
      throw Error(JSON.stringify(response?.response?.data));
    })
  }

  requestEmailToken(email: string, linkId: string, challengeId: string): Promise<any> {
    const payload = linkId
    ? { link_id: linkId, email, challenge_id: challengeId }
    : { email, challenge_id: challengeId }

    return this.api
    .post('request-email-token', payload)
    .then((response) => {
      return response.data
    })
    .catch((err) => {
      throw normalizeAuthError(err);
    })
  }

  verifyEmailCode(email: string, token: string): Promise<any> {
    return this.api
    .post('verify-email-token', { email, token })
    .then((response) => {
      return response.data
    })
    .catch((err) => {
      throw normalizeAuthError(err);
    })
  }

  confirmChallengeToken(challengeToken: string): Promise<any> {
    return this.api
    .post('auth/confirm-challenge', { token: challengeToken })
    .then((response) => {
      return response.data
    })
    .catch((response) => {
      throw Error(JSON.stringify(response?.response?.data));
    })
  }

  verifyChallengeId(challengeId: string): Promise<any> {
    return this.api
    .post('auth/verify-challenge', { challenge_id: challengeId })
    .then((response) => {
      return response.data
    })
    .catch((response) => {
      throw Error(JSON.stringify(response?.response?.data));
    })
  }

  requestSmsToken(number: string, cc: string): Promise<any> {
    return this.api
    .post('request-sms-token', { number, cc, v2: true })
    .then((response) => {
      return response.data
    })
    .catch((err) => {
      throw normalizeAuthError(err);
    })
  }

  verifySmsToken(number: string, cc: string, token: string, userId: string, jwtToken: string): Promise<any> {
    return this.api
    .post('verify-sms-token', { 
      number, cc, token, user_id: userId, v2: true 
    }, {
      headers: {
        'Authorization': `Bearer ${jwtToken}`,
      },
    })
    .then((response) => {
      return response.data
    })
    .catch((err) => {
      throw normalizeAuthError(err);
    })
  }

  // Signup phone verification before the account exists: authenticated by the
  // pending-signup token instead of a JWT. Returns a new signup_token with the
  // verified phone folded in.
  verifySignupSmsToken(number: string, cc: string, token: string, signupToken: string): Promise<any> {
    return this.api
    .post('auth/verify-sms-token', {
      number, cc, token, signup_token: signupToken, v2: true
    })
    .then((response) => {
      return response.data
    })
    .catch((err) => {
      throw normalizeAuthError(err);
    })
  }

  // Final signup step: exchanges the pending-signup token + birthday for the
  // real account. The backend only creates the user row here, after its own
  // 16+ age check.
  completeSignup(signupToken: string, birthday: string): Promise<any> {
    return this.api
    .post('complete-signup', {
      signup_token: signupToken,
      birthday,
    })
    .then((response) => {
      return response.data
    })
    .catch((err) => {
      throw normalizeAuthError(err);
    })
  }

  currentUser(jwtToken: string): Promise<any> {
    return this.api
      .get(`${this.url.replace(/\/v1$/, '/v2')}/user/me`, {
        headers: { 'Authorization': `Bearer ${jwtToken}` },
      })
      .then((response) => response.data)
  }

  // Anonymous callers get the same client details with require_consent: true.
  oauthClientInfo(oauthRequest: string, jwtToken?: string): Promise<any> {
    return this.api
      .get('oauth/client-info', {
        params: { oauth_request: oauthRequest },
        headers: jwtToken ? { 'Authorization': `Bearer ${jwtToken}` } : {},
      })
      .then((response) => response.data)
  }

  oauthConsent(oauthRequest: string, approved: boolean, jwtToken: string): Promise<{ redirect_to: string }> {
    return this.api
      .post('oauth/consent', { oauth_request: oauthRequest, approved }, {
        headers: { 'Authorization': `Bearer ${jwtToken}` },
      })
      .then((response) => response.data)
  }

  updateUserBirthday(birthday: string, jwtToken: string): Promise<any> {
    return this.api
    .post('update-user-single-field', {
      birthday: birthday,
    }, {
      headers: {
        'Authorization': `Bearer ${jwtToken}`,
      },
    })
    .then((response) => {
      return response.data
    })
    .catch((response) => {
      throw Error(JSON.stringify(response?.response?.data));
    })
  }
}

const api = new FetchApi();

export default api;
